import React from 'react';
import { PatternFormat, NumericFormatProps, InputAttributes } from 'react-number-format';

import { TextField } from './textfield.component';
import { PhoneProps } from '../../interfaces';
import { checkRegex } from '../../validations';
import { useValidateInput, useFormContext, useGetFieldRules } from '../../hooks';
import { TooltipContainer } from '../tooltip-container';

export function PhoneInput({ name, ...props }: PhoneProps): JSX.Element {
  const contextRules = useGetFieldRules<PhoneProps>(name);
  return <PhoneComponent {...{ ...contextRules, ...props }} name={name} />;
}

const NumberFormatCustom = React.forwardRef<
  NumericFormatProps<InputAttributes>,
  {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
  }
>(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <PatternFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values: { value: string }): void => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        });
      }}
      format='(###) ###-####'
      mask='_'
    />
  );
});

function PhoneComponent(props: PhoneProps): JSX.Element {
  const getError = (value: unknown): string[] | null => {
    if (!checkRegex(value, '^\\d{10}$')) {
      return [props.customFieldProps?.errorMessages?.invalidPhone ?? 'Invalid phone'];
    }

    return null;
  };

  const { error, validateInput } = useValidateInput(props, getError);
  const { setValueByName } = useFormContext();
  const allProps = { ...props, tooltip: '' };
  const setValue = (newValue: string): void => {
    const value = newValue.replace(/[^\d.]/g, '');
    setValueByName(props.name, value);
    if (props.onChange) {
      props.onChange(value);
    }
    validateInput(value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
  };

  React.useEffect(() => {
    if (props.defaultValue && props.defaultValue !== '') {
      setValue(props.defaultValue);
    }
    if (props.defaultValue != null) validateInput(props.defaultValue);
  }, []);

  return (
    <TooltipContainer tooltip={props.tooltip}>
      <TextField
        {...allProps}
        muiProps={{
          name: props.name,
          onChange: handleChange,
          helperText: error ? error : props.helperText,
          error: error != null,
          InputProps: {
            inputComponent: NumberFormatCustom as any
          }
        }}
      />
    </TooltipContainer>
  );
}
